import React, { useEffect, useRef, useState } from "react";
import { Button, Card, CardBody, CardHeader, Col, Container, Input, Label, Spinner, Table } from "reactstrap";
import Breadcrumbs from '../../../components/Common/Breadcrumb';
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_blue.css";
import { API_BASE_URL } from "../../../constants";
import axios from "axios";
import profileDefault from "../../../assets/images/icons/profile.png";
import { Toast } from "primereact/toast";
import { ChartNoAxesCombined } from "lucide-react";
import ReactApexChart from "react-apexcharts";

const StaffAttendance = () => {
    const breadcrumbItems = [
        { title: "Staffs", link: "/" },
        { title: "Attendance", link: "#!" },
    ];

    const [attendanceData, setAttendanceData] = useState({});
    const [staffList, setStaffList] = useState([]); // State to store staff list
    const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]); // Default to today's date
    const [loading, setLoading] = useState(false); // Loading state for API calls
    const [setAllStatus, setSetAllStatus] = useState("");
    const [loadingRender, setLoadingRender] = useState(false);
    const [analyticsData, setAnalyticsData] = useState(null);
    const toast = useRef(null);

    const isFutureDate = (date) => {
        const today = new Date().toISOString().split('T')[0]; // Get today's date in YYYY-MM-DD format
        return date > today;
    };

    // Fetch staff data based on the selected date
    const fetchStaffAttendance = React.useCallback(async () => {
        if (isFutureDate(selectedDate)) {
            toast.current.show({
                severity: "warn",
                summary: "Warning",
                detail: "Cannot fetch attendance for future dates.",
                life: 3000,
            });
            return;
        }
        setLoading(true);
        try {
            const response = await axios.get(`${API_BASE_URL}/staff-attendance/get?date=${selectedDate}`, {
                headers: {
                    Authorization: "Bearer " + localStorage.getItem("token"),
                },
            });

            console.log("API Response:", response.data); // Debugging

            if (response.data?.status === "success") {
                setStaffList(response.data.data); // Set staffList with the fetched data

                // Initialize attendanceData with the fetched attendance records
                const initialAttendanceData = {};
                response.data.data.forEach((staff) => {
                    console.log(`Staff ID: ${staff.id}, Attendances:`, staff.attendances); // Debugging

                    if (staff.attendances?.length > 0) {
                        const attendance = staff.attendances[0];
                        initialAttendanceData[staff.id] = {
                            status: attendance.status || "Not Marked",
                            check_in: attendance.status === "leave" || attendance.status === "absent" ? "" : attendance.check_in || "09:00:00", // Exclude check_in for leave or absent
                            check_out: attendance.status === "leave" || attendance.status === "absent" ? "" : attendance.check_out || "18:00:00", // Exclude check_out for leave or absent
                            remarks: attendance.remarks || "",
                        };
                    } else {
                        initialAttendanceData[staff.id] = {
                            status: "Not Marked",
                            check_in: "09:00:00",
                            check_out: "18:00:00",
                            remarks: "",
                        };
                    }
                });
                setAttendanceData(initialAttendanceData);
            } else {
                setStaffList([]); // Clear the list if no data is returned
                setAttendanceData({});
            }
        } catch (error) {
            console.error("Error fetching staff attendance:", error.response?.data || error);
            toast.current.show({ severity: "error", summary: "Error", detail: "Failed to fetch staff attendance data", life: 3000 });
        } finally {
            setLoading(false);
        }
    }, [selectedDate]);

    const handleSetAllStatusChange = (status) => {
        setSetAllStatus(status);

        const updatedAttendanceData = { ...attendanceData };
        staffList.forEach((staff) => {
            updatedAttendanceData[staff.id] = {
                ...updatedAttendanceData[staff.id],
                status: status,
                check_in: status === "leave" || status === "absent" ? "" : "09:00:00",
                check_out: status === "leave" || status === "absent" ? "" : "18:00:00",
            };
        });

        setAttendanceData(updatedAttendanceData);
    };
    // Handle attendance status change
    const handleAttendanceChange = (staffId, status) => {
        setAttendanceData((prevData) => ({
            ...prevData,
            [staffId]: {
                ...prevData[staffId],
                status: status,
            },
        }));
    };

    const handleCheckInChange = (staffId, checkIn) => {
        setAttendanceData((prevData) => ({
            ...prevData,
            [staffId]: {
                ...prevData[staffId],
                check_in: checkIn,
            },
        }));
    };

    const handleCheckOutChange = (staffId, checkOut) => {
        setAttendanceData((prevData) => ({
            ...prevData,
            [staffId]: {
                ...prevData[staffId],
                check_out: checkOut,
            },
        }));
    };

    // Handle remarks change
    const handleRemarksChange = (staffId, remarks) => {
        setAttendanceData((prevData) => ({
            ...prevData,
            [staffId]: {
                ...prevData[staffId],
                remarks: remarks,
            },
        }));
    };

    const fetchAttendanceAnalytics = React.useCallback(async () => {
        if (isFutureDate(selectedDate)) {
            toast.current.show({
                severity: "warn",
                summary: "Warning",
                detail: "Cannot fetch analytics for future dates.",
                life: 3000,
            });
            return;
        }
        try {
            const response = await axios.get(`${API_BASE_URL}/staff-attendance/analysis?date=${selectedDate}`, {
                headers: {
                    Authorization: "Bearer " + localStorage.getItem("token"),
                },
            });

            if (response.data?.status === "success") {
                setAnalyticsData(response.data.data); // Set analytics data
            } else {
                setAnalyticsData(null); // Clear analytics data if no data is returned
            }
        } catch (error) {
            console.error("Error fetching attendance analytics:", error.response?.data || error);
            toast.current.show({ severity: "error", summary: "Error", detail: "Failed to fetch attendance analytics", life: 3000 });
        }
    }, [selectedDate]);

    const scrollToAnalytics = () => {
        const analyticsSection = document.getElementById("attendance-analytics");
        if (analyticsSection) {
            analyticsSection.scrollIntoView({ behavior: "smooth" });
        }
    };
    // Handle date change
    const handleDateChange = (date) => {
        const localDate = new Date(date[0].getTime() - date[0].getTimezoneOffset() * 60000);
        const formattedDate = localDate.toISOString().split('T')[0];
        setSelectedDate(formattedDate);

        if (isFutureDate(formattedDate)) {
            toast.current.show({
                severity: "warn",
                summary: "Warning",
                detail: "Cannot mark attendance for future dates.",
                life: 3000,
            });
        }
    };

    // Handle attendance submission for all staff members
    const handleSubmitAll = async () => {
        setLoadingRender(true);
        if (isFutureDate(selectedDate)) {
            toast.current.show({
                severity: "warn",
                summary: "Warning",
                detail: "Cannot submit attendance for future dates.",
                life: 3000,
            });
            return;
        }
        try {
            // Filter staff members whose attendance has been explicitly marked or modified
            const attendances = staffList
                .filter((staff) => {
                    const attendance = attendanceData[staff.id] || {};
                    return attendance.status && attendance.status !== "Not Marked"; // Only include staff with marked attendance
                })
                .map((staff) => {
                    const attendance = attendanceData[staff.id] || {};

                    // Ensure check-in and check-out are in HH:mm:ss format
                    const formattedCheckIn = attendance.check_in.includes(":") && attendance.check_in.length === 5
                        ? `${attendance.check_in}:00`
                        : attendance.check_in;

                    const formattedCheckOut = attendance.check_out.includes(":") && attendance.check_out.length === 5
                        ? `${attendance.check_out}:00`
                        : attendance.check_out;

                    // If the status is "leave" or "absent", exclude check_in and check_out
                    const payloadForStaff = {
                        staff_id: staff.id,
                        date: selectedDate,
                        status: attendance.status || "Not Marked",
                        remarks: attendance.remarks || "",
                    };

                    if (attendance.status !== "leave" && attendance.status !== "absent") {
                        // Include check_in and check_out only if the status is not "leave" or "absent"
                        payloadForStaff.check_in = formattedCheckIn || "09:00:00";
                        payloadForStaff.check_out = formattedCheckOut || "18:00:00";
                    }

                    return payloadForStaff;
                });

            // Validate the payload
            const invalidStatuses = attendances.filter(data =>
                !["present", "absent", "half-day", "leave"].includes(data.status)
            );

            const invalidTimes = attendances.filter(data =>
                data.status !== "leave" && data.status !== "absent" && (
                    !/^([01]?[0-9]|2[0-3]):[0-5][0-9]:[0-5][0-9]$/.test(data.check_in) ||
                    !/^([01]?[0-9]|2[0-3]):[0-5][0-9]:[0-5][0-9]$/.test(data.check_out)
                )
            );

            if (invalidStatuses.length > 0 || invalidTimes.length > 0) {
                toast.current.show({
                    severity: "warn",
                    summary: "Validation Error",
                    detail: "Please ensure all fields are valid (status, check-in, check-out)",
                    life: 3000,
                });
                return;
            }

            // Wrap the payload in an object with the "attendances" field
            const payload = {
                attendances: attendances,
            };

            // Debug: Log the payload
            console.log("Submitting attendance data:", payload);

            // Send the payload to the server
            const response = await axios.post(`${API_BASE_URL}/staff-attendance/add`, payload, {
                headers: {
                    Authorization: "Bearer " + localStorage.getItem("token"),
                },
            });

            // Handle the response
            if (response.data?.status === "success") {
                toast.current.show({
                    severity: "success",
                    summary: "Success",
                    detail: "Attendance updated successfully!",
                    life: 3000,
                });
                fetchStaffAttendance(); // Refresh the list
                fetchAttendanceAnalytics();
            } else {
                toast.current.show({ severity: "error", summary: "Error", detail: "Failed to update attendance", life: 3000 });
            }
            setLoadingRender(false);
        } catch (error) {
            console.error("Error updating attendance:", error.response?.data || error);
            // Display validation errors from the server
            if (error.response?.data?.errors) {
                console.log("Validation Errors:", error.response.data.errors); // Debugging
                const errorMessages = Object.values(error.response.data.errors).join(", ");
                toast.current.show({ severity: "error", summary: "Validation Error", detail: errorMessages, life: 3000 });
            } else {
                toast.current.show({ severity: "error", summary: "Error", detail: "Failed to update attendance", life: 3000 });
            }
        } finally {
            setLoadingRender(false);
        }
    };

    // Fetch staff data on component mount and when selectedDate changes
    useEffect(() => {
        fetchStaffAttendance();
        fetchAttendanceAnalytics();
    }, [fetchStaffAttendance, fetchAttendanceAnalytics]);

    const getAttendanceChartOptions = () => {
        if (!analyticsData) return null;

        return {
            series: [
                analyticsData.present,
                analyticsData.absent,
                analyticsData.half_day,
                analyticsData.leave
            ],
            chart: {
                type: 'donut',
                height: 350
            },
            labels: ['Present', 'Absent', 'Half-day', 'Leave'],
            colors: ['#34c38f', '#f46a6a', '#50a5f1', '#f1b44c'],
            legend: {
                position: 'right',
                // offsetY:50,
                // height:230,
            },
            responsive: [{
                breakpoint: 480,
                options: {
                    chart: {
                        width: 300
                    },
                    legend: {
                        position: 'bottom'
                    }
                }
            }],
            tooltip: {
                y: {
                    formatter: function (value) {
                        return value + " staff";
                    }
                }
            }
        };
    };


    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <Toast ref={toast} />
                    <Breadcrumbs title="Staff Attendance" breadcrumbItems={breadcrumbItems} />
                    <Card>
                        <CardHeader className="align-items-center d-flex justify-content-between flex-wrap">
                            {/* Parent List Heading */}
                            <h4 className="card-title mb-0 flex-grow-1">Attendance</h4>

                            {/* Analytics Button and Search Options */}
                            <div className="d-none d-md-flex gap-2">
                                {/* Analytics Button (Visible on Larger Screens) */}
                                <Button
                                    color="success"
                                    className="d-flex align-items-center"
                                    onClick={scrollToAnalytics}
                                    style={{
                                        height: "35px",
                                        fontSize: "14px",
                                        padding: "0 12px"
                                    }}
                                >
                                    <ChartNoAxesCombined className="mdi mdi-chart-line me-1" style={{ fontSize: "16px" }} />
                                    <span>Analytics</span>
                                </Button>

                                {/* Date Picker and Search Button (Visible on Larger Screens) */}
                                <div className="d-flex gap-2">
                                    <Flatpickr
                                        className="form-control d-block"
                                        placeholder="Select Date"
                                        value={selectedDate}
                                        onChange={handleDateChange}
                                        options={{
                                            altInput: true,
                                            altFormat: "F j, Y",
                                            dateFormat: "Y-m-d",
                                            maxDate: "today",
                                        }}
                                        style={{ height: "35px", fontSize: "14px", padding: "2px 8px" }}
                                    />
                                    <Button
                                        color="primary"
                                        onClick={fetchStaffAttendance}
                                        style={{ height: "35px", fontSize: "14px", padding: "2px 10px" }}
                                    >
                                        Search
                                    </Button>
                                </div>
                            </div>
                        </CardHeader>

                        {/* Mobile View: Analytics Button and Search Options Below Parent List Heading */}
                        <div className="d-block d-md-none mt-3">
                            <div className="d-flex flex-column gap-2">
                                {/* Analytics Button (Visible on Mobile Screens) */}
                                <Button
                                    color="success"
                                    className="d-flex align-items-center justify-content-center w-100"
                                    onClick={scrollToAnalytics}
                                    style={{
                                        height: "35px",
                                        fontSize: "14px",
                                        padding: "0 12px"
                                    }}
                                >
                                    <ChartNoAxesCombined className="mdi mdi-chart-line me-1" style={{ fontSize: "16px" }} />
                                    <span>Analytics</span>
                                </Button>

                                {/* Date Picker and Search Button (Visible on Mobile Screens) */}
                                <div className="d-flex flex-column gap-2">
                                    <Flatpickr
                                        className="form-control d-block w-100"
                                        placeholder="Select Date"
                                        value={selectedDate}
                                        onChange={handleDateChange}
                                        options={{
                                            altInput: true,
                                            altFormat: "F j, Y",
                                            dateFormat: "Y-m-d",
                                            maxDate: "today",
                                        }}
                                        style={{ height: "35px", fontSize: "14px", padding: "2px 8px" }}
                                    />
                                    <Button
                                        color="primary"
                                        onClick={fetchStaffAttendance}
                                        className="w-100"
                                        style={{ height: "35px", fontSize: "14px", padding: "2px 10px" }}
                                    >
                                        Search
                                    </Button>
                                </div>
                            </div>
                        </div>
                        <CardBody>
                            {/* Set Attendance for All Section */}
                            <div className="mb-4">
                                <Label className="me-2">Set Attendance for All:</Label>
                                <div className="form-check form-check-inline">
                                    <Input className="form-check-input" type="radio" id="present" name="set-all-attendance" value="present"
                                        checked={setAllStatus === "present"} onChange={(e) => handleSetAllStatusChange(e.target.value)} />
                                    <Label className="form-check-label" for="present">Present</Label>
                                </div>
                                <div className="form-check form-check-inline">
                                    <Input className="form-check-input" type="radio" id="absent" name="set-all-attendance" value="absent"
                                        checked={setAllStatus === "absent"} onChange={(e) => handleSetAllStatusChange(e.target.value)} />
                                    <Label className="form-check-label" for="absent">Absent</Label>
                                </div>
                                <div className="form-check form-check-inline">
                                    <Input className="form-check-input" type="radio" id="half-day" name="set-all-attendance" value="half-day"
                                        checked={setAllStatus === "half-day"} onChange={(e) => handleSetAllStatusChange(e.target.value)} />
                                    <Label className="form-check-label" for="half-day">Half-day</Label>
                                </div>
                                <div className="form-check form-check-inline">
                                    <Input className="form-check-input" type="radio" id="leave" name="set-all-attendance" value="leave"
                                        checked={setAllStatus === "leave"} onChange={(e) => handleSetAllStatusChange(e.target.value)} />
                                    <Label className="form-check-label" for="leave">Leave</Label>
                                </div>
                            </div>
                            <div className="table-responsive">
                                <Table bordered className="mb-0">
                                    <thead>
                                        <tr className="table-info">
                                            <th>SI.No</th>
                                            <th>Photo</th>
                                            <th>Name</th>
                                            <th>Details</th>
                                            <th>Attendance</th>
                                            <th>Check-In</th>
                                            <th>Check-Out</th>
                                            <th>Remarks</th>
                                            <th>Current Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {loading ? (
                                            <tr>
                                                <td colSpan="9" className="text-center">
                                                    <div className="spinner-border text-primary" role="status">
                                                        <span className="visually-hidden">Loading...</span>
                                                    </div>
                                                </td>
                                            </tr>
                                        ) : staffList.length > 0 ? (
                                            staffList.map((staff, index) => {
                                                const attendance = attendanceData[staff.id] || {};
                                                const attendanceStatus = attendance.status || "Not Marked";
                                                const checkIn = attendance.check_in || "09:00:00";
                                                const checkOut = attendance.check_out || "18:00:00";
                                                const remarks = attendance.remarks || "";

                                                return (
                                                    <tr key={staff.id}>
                                                        <th scope="row">{index + 1}</th>
                                                        <td>
                                                            <img src={staff.profile_pic || profileDefault} alt="Staff" className="rounded-circle" width="50" height="50" />
                                                        </td>
                                                        <td>{staff.name}</td>
                                                        <td>
                                                            <ul className="list-unstyled mb-0">
                                                                <li>Employee ID: {staff.employee_id}</li>
                                                                <li>Department: {staff.department}</li>
                                                                <li>Designation: {staff.designation}</li>
                                                            </ul>
                                                        </td>
                                                        <td>
                                                            {/* Attendance status radio buttons */}
                                                            <div className="form-check form-check-inline">
                                                                <Input className="form-check-input" type="radio" id={`present-${staff.id}`} name={`attendance-${staff.id}`} value="present"
                                                                    checked={attendanceStatus === "present"} onChange={(e) => handleAttendanceChange(staff.id, e.target.value)} />
                                                                <Label className="form-check-label" htmlFor={`present-${staff.id}`} >Present</Label>
                                                            </div>
                                                            <div className="form-check form-check-inline">
                                                                <Input className="form-check-input" type="radio" id={`absent-${staff.id}`} name={`attendance-${staff.id}`} value="absent"
                                                                    checked={attendanceStatus === "absent"} onChange={(e) => handleAttendanceChange(staff.id, e.target.value)} />
                                                                <Label className="form-check-label" htmlFor={`absent-${staff.id}`}>Absent</Label>
                                                            </div>
                                                            <div className="form-check form-check-inline">
                                                                <Input className="form-check-input" type="radio" id={`half-day-${staff.id}`} name={`attendance-${staff.id}`} value="half-day"
                                                                    checked={attendanceStatus === "half-day"} onChange={(e) => handleAttendanceChange(staff.id, e.target.value)} />
                                                                <Label className="form-check-label" htmlFor={`half-day-${staff.id}`}>Halfday</Label>
                                                            </div>
                                                            <div className="form-check form-check-inline">
                                                                <Input className="form-check-input" type="radio" id={`leave-${staff.id}`} name={`attendance-${staff.id}`} value="leave"
                                                                    checked={attendanceStatus === "leave"} onChange={(e) => handleAttendanceChange(staff.id, e.target.value)} />
                                                                <Label className="form-check-label" htmlFor={`leave-${staff.id}`}>Leave</Label>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <Input
                                                                type="time"
                                                                value={checkIn.split(":").slice(0, 2).join(":")} // Remove seconds for display
                                                                onChange={(e) => handleCheckInChange(staff.id, e.target.value)}
                                                                disabled={attendanceStatus === "leave" || attendanceStatus === "absent"} // Disable if status is "leave" or "absent"
                                                            />
                                                        </td>
                                                        <td>
                                                            <Input
                                                                type="time"
                                                                value={checkOut.split(":").slice(0, 2).join(":")} // Remove seconds for display
                                                                onChange={(e) => handleCheckOutChange(staff.id, e.target.value)}
                                                                disabled={attendanceStatus === "leave" || attendanceStatus === "absent"} // Disable if status is "leave" or "absent"
                                                            />
                                                        </td>
                                                        <td>
                                                            <Input
                                                                type="text"
                                                                value={remarks}
                                                                onChange={(e) => handleRemarksChange(staff.id, e.target.value)}
                                                            />
                                                        </td>
                                                        <td>
                                                            <span className={`badge ${attendanceStatus === "present" ? "bg-success-subtle text-success" :
                                                                attendanceStatus === "absent" ? "bg-danger-subtle text-danger" :
                                                                    attendanceStatus === "half-day" ? "bg-info-subtle text-info" :
                                                                        attendanceStatus === "leave" ? "bg-secondary-subtle text-secondary" : "bg-light text-dark"} text-uppercase`}>
                                                                {attendanceStatus}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        ) : (
                                            <tr>
                                                <td colSpan="9" className="text-center">No staff found for the selected date.</td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>
                            {/* Submit Button at the Bottom */}
                            <div className="text-end mt-3">
                                <Button color="primary" onClick={handleSubmitAll}>
                                    Submit Attendance
                                    {loadingRender && <Spinner className="ms-1" size="sm">Loading...</Spinner>}
                                </Button>
                            </div>
                        </CardBody>
                    </Card>

                    <Col lg={6}>
                        {/* Attendance Analytics Section */}
                        <Card id="attendance-analytics" className="mt-4">
                            <CardHeader>
                                <h4 className="card-title mb-0">Attendance Analytics</h4>
                            </CardHeader>
                            <CardBody>
                                {analyticsData ? (
                                    <div>
                                        {/* Replace the Chart.js Bar with ApexCharts Donut */}
                                        <div className="mb-4">
                                            <h5>Attendance Distribution</h5>
                                            <div id="attendance-donut-chart">
                                                <ReactApexChart
                                                    options={getAttendanceChartOptions()}
                                                    series={getAttendanceChartOptions().series}
                                                    type="donut"
                                                    height={350}
                                                />
                                            </div>
                                        </div>

                                        {/* Additional Analytics Data */}
                                        <div>
                                            <h5>Summary</h5>
                                            <ul>
                                                <li>Total Staff: {analyticsData.total_staff}</li>
                                                <li>Present: {analyticsData.present}</li>
                                                <li>Absent: {analyticsData.absent}</li>
                                                <li>Half-day: {analyticsData.half_day}</li>
                                                <li>Leave: {analyticsData.leave}</li>
                                            </ul>
                                        </div>
                                    </div>
                                ) : (
                                    <p>No analytics data available for the selected date.</p>
                                )}
                            </CardBody>
                        </Card>
                    </Col>
                </Container>
            </div>
        </React.Fragment>
    );
};

export default StaffAttendance;